<template>
  <div class="emoji-list-card">
    <!-- Header -->
    <div class="emoji-list-header">
      <h2 class="emoji-list-title">{{ title }}</h2>
      <p class="emoji-list-subtitle">{{ subtitle }}</p>
    </div>

    <!-- Sin datos -->
    <div v-if="!emojis.length" class="emoji-no-data">
      <div class="emoji-no-data-illustration">
        <ion-icon name="remove-circle-outline"></ion-icon>
      </div>
      <h3 class="emoji-no-data-title">No hay datos para mostrar</h3>
      <p class="emoji-no-data-text">Aún no hay interacciones con emojis en este rango.</p>
    </div>

    <!-- Lista de Emojis (paginada) -->
    <div v-else class="emoji-list-wrapper">
      <div
        v-for="(item, localIndex) in pagedEmojis"
        :key="rowKey(item, localIndex)"
        class="emoji-list-block"
      >
        <!-- Fila principal -->
        <div class="emoji-list-row">
          <div class="emoji-cell" :title="item.emoji">{{ item.emoji }}</div>
          <div class="count-cell">{{ item.total_usos }} usos</div>
          <div class="message-cell" :title="item.first_message">
            {{ item.first_message }}
          </div>

          <button
            v-if="enableDropdown"
            class="toggle-btn"
            type="button"
            :aria-expanded="isOpen(item)"
            :aria-controls="`contacts-${rowKey(item, localIndex)}`"
            @click="toggle(item)"
            title="Mostrar/Ocultar contactos"
          >
            <ion-icon :name="isOpen(item) ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
          </button>
        </div>

        <!-- Dropdown de contactos -->
        <transition name="fade">
          <div
            v-if="enableDropdown && isOpen(item)"
            class="emoji-contacts-drop"
            :id="`contacts-${rowKey(item, localIndex)}`"
            role="region"
            :aria-label="`Contactos para emoji ${item.emoji}`"
          >
            <div v-if="(item.contacts || []).length === 0" class="no-contacts">
              <ion-icon name="information-circle-outline"></ion-icon>
              <span>Sin contactos en este rango.</span>
            </div>

            <div v-else class="table-wrap">
              <table class="contacts-table">
                <thead>
                  <tr>
                    <th>Línea</th>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th class="th-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in (item.contacts || [])" :key="contactKey(c)">
                    <td>{{ c.line_name || '—' }}</td>
                    <td>{{ c.name || '—' }}</td>
                    <td>{{ c.phone_number || '—' }}</td>
                    <td class="td-actions">
                      <button class="icon-btn chat" type="button" @click="$emit('chat', c)" title="Ir al chat">
                        <ion-icon name="chatbubble-ellipses-outline" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </transition>
      </div>

      <!-- Controles de paginación -->
      <div class="pager" v-if="totalPages > 1">
        <button
          class="pager-btn"
          type="button"
          :disabled="currentPage === 1"
          @click="goPrev"
        >
          <ion-icon name="chevron-back-outline" />
          <span>Anterior</span>
        </button>

        <span class="pager-status">
          Página {{ currentPage }} de {{ totalPages }}
        </span>

        <button
          class="pager-btn"
          type="button"
          :disabled="currentPage === totalPages"
          @click="goNext"
        >
          <span>Siguiente</span>
          <ion-icon name="chevron-forward-outline" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmojiTopList',
  props: {
    emojis: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: 'Top Emojis'
    },
    subtitle: {
      type: String,
      default: ''
    },
    enableDropdown: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      openByKey: {},     
      currentPage: 1    
    }
  },
  computed: {
    safeEmojis() {
      return Array.isArray(this.emojis) ? this.emojis : []
    },
    totalItems() {
      return this.safeEmojis.length
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.pageSize))
    },
    pagedEmojis() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.safeEmojis.slice(start, end)
    }
  },
  watch: {
    emojis() {
      this.currentPage = 1
      this.openByKey = {}
    }
  },
  methods: {
    rowKey(item, idx) {
      return item?.hex ? `hex-${item.hex}` : `idx-${idx}`
    },
    contactKey(c) {
      const cid = c?.contact_id ?? 'x'
      const lid = c?.line_id ?? 'y'
      return `c-${cid}-l-${lid}`
    },
    isOpen(item) {
      const k = this.rowKey(item, 0) 
      return !!this.openByKey[k]
    },
    toggle(item) {
      const k = this.rowKey(item, 0)
      this.$set(this.openByKey, k, !this.openByKey[k])
    },
    goPrev() {
      if (this.currentPage > 1) this.currentPage -= 1
    },
    goNext() {
      if (this.currentPage < this.totalPages) this.currentPage += 1
    }
  }
}
</script>

<style scoped>
.emoji-list-card {
  background-color: #ffffff;
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  border: 1px solid #e2e8f0;
  margin-top: 2rem;
  font-family: 'Manrope', sans-serif;
}

.emoji-list-header {
  margin-bottom: 1rem;
  text-align: left;
}

.emoji-list-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.emoji-list-subtitle {
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

.emoji-list-wrapper {
  display: flex;
  flex-direction: column;
}

.emoji-list-block {
  display: block;
}

.emoji-list-row {
  display: grid;
  grid-template-columns: 60px 120px 1fr 44px;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;
  gap: 1rem;
}

.emoji-cell {
  font-size: 1.5rem;
  text-align: center;
}

.count-cell {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
}

.message-cell {
  color: #64748b;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toggle-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
}
.toggle-btn ion-icon {
  font-size: 18px;
  color: #334155;
}
.toggle-btn:hover {
  background: #eef2ff;
  transform: translateY(-1px);
}

.emoji-contacts-drop {
  background: #f9fafb;
  border-left: 3px solid #6366f1;
  border-radius: 10px;
  margin: 0.5rem 0 1rem 0;
  padding: 0.75rem;
}

.table-wrap {
  overflow-x: auto;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.contacts-table th,
.contacts-table td {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}
.contacts-table thead th {
  font-weight: 700;
  color: #0f172a;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}
.th-actions,
.td-actions {
  text-align: right;
}

.icon-btn {
  all: unset;
  cursor: pointer;
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  border-radius: 8px;
  transition: all 0.25s ease;
}

.icon-btn.chat {
  color: #2563eb;
  border: 1.5px solid rgba(37, 99, 235, 0.25);
  background-color: rgba(37, 99, 235, 0.05);
}
.icon-btn.chat:hover {
  background-color: rgba(37, 99, 235, 0.15);
  transform: translateY(-1px) scale(1.05);
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}
.pager-status {
  color: #475569;
  font-size: 0.9rem;
}
.pager-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  padding: 6px 10px;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}
.pager-btn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}
.pager-btn:hover:not([disabled]) {
  background: #f8fafc;
  transform: translateY(-1px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ===== No Data ====== */
.emoji-no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  text-align: center;
  padding: 2rem 1rem;
  border: 2px dashed #e2e8f0;
  border-radius: .75rem;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.emoji-no-data-illustration {
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: #eef2ff;
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.emoji-no-data-illustration ion-icon {
  font-size: 1.4rem;
  color: #64748b;
}

.emoji-no-data-title {
  margin: 0;
  font-weight: 700;
  font-size: .95rem;
  color: #0f172a;
}

.emoji-no-data-text {
  margin: 0;
  font-weight: 500;
  font-size: .85rem;
  color: #64748b;
}
</style>
